import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Network } from 'lucide-react';
import { DOMAIN } from '../constants';
import { Reveal, SectionHero } from '../components/UI';

export const Sitemap: React.FC = () => {
  const pageTitle = "Plan du Site | Structure | Béton Mobile des Cantons";
  const pageDesc = "Plan du site complet de Béton Mobile des Cantons. Accédez facilement à nos services de béton mobile, plancher radiant, coffrage et réalisations.";
  const canonicalUrl = `${DOMAIN}/plan-du-site`;

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${DOMAIN}/images/logo-beton-mobile-des-cantons-fr.png`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={`${DOMAIN}/images/logo-beton-mobile-des-cantons-fr.png`} />
      </Helmet>
      
      <SectionHero 
        title="Plan du site" 
        subtitle="Structure Globale" 
        image="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=2070&auto=format&fit=crop"
        height="h-[40vh]"
      />
      
      <div className="container mx-auto px-6 py-20">
         <Reveal>
           <div className="max-w-4xl mx-auto bg-white p-12 border border-slate-200 shadow-xl relative overflow-hidden">
             <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-20 pointer-events-none"></div>
             <div className="flex items-center gap-4 mb-10 border-b-2 border-slate-900 pb-4">
               <Network size={40} className="text-brand-red" />
               <h2 className="text-3xl font-display font-bold text-slate-900 uppercase">Architecture du site</h2>
             </div>
             <div className="grid md:grid-cols-2 gap-12 relative z-10">
               <div>
                 <h3 className="text-xl font-display font-bold text-brand-red mb-6 uppercase tracking-wider flex items-center gap-2">
                   <div className="w-2 h-2 bg-brand-red"></div> Pages Principales
                 </h3>
                 <ul className="space-y-4 font-mono text-sm border-l border-slate-200 pl-6 ml-1">
                   {[
                     { to: "/", label: "Accueil" },
                     { to: "/beton", label: "Béton Mobile" },
                     { to: "/plancher-radiant", label: "Plancher Radiant" },
                     { to: "/coffrage-et-fondation", label: "Coffrage et Fondation" },
                     { to: "/services", label: "Services et Produits" },
                     { to: "/realisations", label: "Nos Réalisations" },
                     { to: "/contact", label: "Contact et Soumission" },
                   ].map((link, i) => (
                     <li key={i} className="relative">
                       <span className="absolute -left-6 top-1/2 -translate-y-1/2 w-4 h-px bg-slate-300"></span>
                       <Link to={link.to} className="text-slate-700 hover:text-brand-red hover:underline decoration-brand-red underline-offset-4 transition-colors font-bold uppercase">
                         {link.label}
                       </Link>
                     </li>
                   ))}
                 </ul>
               </div>
               <div>
                 <h3 className="text-xl font-display font-bold text-brand-red mb-6 uppercase tracking-wider flex items-center gap-2">
                   <div className="w-2 h-2 bg-brand-red"></div> Sections Techniques
                 </h3>
                 <ul className="space-y-8 font-mono text-sm border-l border-slate-200 pl-6 ml-1">
                   <li className="relative">
                     <span className="absolute -left-6 top-3 w-4 h-px bg-slate-300"></span>
                     <span className="text-slate-400 font-bold uppercase text-xs block mb-2">Sous la section Béton</span>
                     <ul className="space-y-2 pl-4">
                       <li className="text-slate-600">• Dalle bouchardée</li>
                       <li className="text-slate-600">• Dalle poncée (Mat, Satiné, Brillant)</li>
                       <li className="text-slate-600">• Coloration du béton</li>
                     </ul>
                   </li>
                   <li className="relative">
                     <span className="absolute -left-6 top-3 w-4 h-px bg-slate-300"></span>
                     <span className="text-slate-400 font-bold uppercase text-xs block mb-2">Sous la section Plancher Radiant</span>
                     <ul className="space-y-2 pl-4">
                       <li className="text-slate-600">• Chaudière Mini BTH</li>
                       <li className="text-slate-600">• Chaudière Mini Ultra</li>
                       <li className="text-slate-600">• Installation pour auto-constructeur</li>
                     </ul>
                   </li>
                   <li className="relative">
                     <span className="absolute -left-6 top-3 w-4 h-px bg-slate-300"></span>
                     <span className="text-slate-400 font-bold uppercase text-xs block mb-2">Sous la section Services</span>
                     <ul className="space-y-2 pl-4">
                       <li className="text-slate-600">• Location d'outils</li>
                       <li className="text-slate-600">• Vente de produits (Sable, Pierre, etc.)</li>
                     </ul>
                   </li>
                 </ul>
               </div>
             </div>
             <div className="mt-12 pt-8 border-t border-slate-200 flex justify-center">
               <Link to="/" className="bg-slate-900 text-white px-8 py-3 font-display font-bold uppercase hover:bg-brand-red transition-colors">
                 Retour à l'accueil
               </Link>
             </div>
           </div>
         </Reveal>
      </div>
    </div>
  );
};